const BASE = "https://signup-extension-data.s3.amazonaws.com";

export interface StoryRecord {
  _id: string;
  title: string;
  html: string;
  content?: string;
  author?: string;
  date?: string;
  media?: { type: string; value: string };
  hidden?: boolean;
}

/**
 * Stories come from S3. Localhost gets the small dev list unless
 * REACT_APP_STORIES_DATA=prod is set (see .env.local), which is useful
 * when a bug only shows up with the full production list.
 */
export const storiesUrl = () => {
  const isLocal = window.location.hostname === "localhost";
  const forceProd = process.env.REACT_APP_STORIES_DATA === "prod";
  return isLocal && !forceProd
    ? `${BASE}/stories-dev.json`
    : `${BASE}/stories.json`;
};

// One download per session, shared by the list and detail pages so that
// moving between them never blanks the screen waiting on the network.
let loaded: StoryRecord[] | undefined;
let pending: Promise<StoryRecord[]> | undefined;

/** The stories if they have already been downloaded, otherwise undefined. */
export const getLoadedStories = () => loaded;

export const loadStories = (): Promise<StoryRecord[]> => {
  if (loaded) return Promise.resolve(loaded);
  if (!pending) {
    pending = (async () => {
      try {
        const data: StoryRecord[] = await (await fetch(storiesUrl())).json();
        loaded = data;
        return data;
      } finally {
        pending = undefined;
      }
    })();
  }
  return pending;
};

/** The stories a list may show, newest first. */
export const visibleStories = (data: StoryRecord[]) =>
  data
    .filter((story) => !story.hidden)
    .sort(
      (a, b) => new Date(b?.date).getTime() - new Date(a?.date).getTime()
    );

// Kept referenced so the decoded image stays in the memory cache.
let warmedImage: HTMLImageElement | undefined;

/**
 * Fetches the list ahead of time and prefetches the image of the newest
 * story, so arriving on the Stories page paints a complete first card
 * instead of an image popping in after the transition. Resolves with the
 * URL that was warmed, or null when there was nothing to warm.
 */
export const warmStories = async (): Promise<string | null> => {
  try {
    const first = visibleStories(await loadStories())[0];
    if (first?.media?.type !== "image" || !first.media.value) return null;
    if (warmedImage?.src === first.media.value) return warmedImage.src;
    const img = new Image();
    img.src = first.media.value;
    warmedImage = img;
    await img.decode?.().catch(() => undefined);
    return img.src;
  } catch {
    return null;
  }
};

export const PAGE_SIZE = 3;

/**
 * What the list page was showing when the reader left it. Restored when they
 * come back so the page has the same height and the browser's scroll
 * restoration lands where they were.
 */
export const listMemory = { search: "", shown: PAGE_SIZE };

/** Test hook: forget anything downloaded or remembered so far. */
export const resetStoriesCache = () => {
  loaded = undefined;
  pending = undefined;
  listMemory.search = "";
  listMemory.shown = PAGE_SIZE;
};
